import type { Lang, MenuItem, PriceMap } from '../types';

let idCounter = 1;
const perCat: Record<string, number> = {};

/**
 * `gu` is transcribed from the printed menu, which is bilingual. `hi` is a
 * Devanagari transliteration of the same name — the board carries no Hindi, so
 * treat it as a starting point and correct it from Menu -> Manage if a dish
 * reads oddly. Either may be empty; the English name is the fallback.
 */
function it(
  name: string,
  cat: string,
  prices: PriceMap,
  hi = '',
  gu = '',
): MenuItem {
  perCat[cat] = (perCat[cat] ?? 0) + 1;
  return {
    id: 'i' + idCounter++,
    name,
    nameHi: hi,
    nameGu: gu,
    cat,
    prices,
    sortOrder: perCat[cat] * 10,
    active: true,
  };
}

/**
 * Digitized from the physical menu. Prices in INR.
 * Fallback only — the live menu comes from the database. See FALLBACK_CATEGORIES.
 */
export const FALLBACK_ITEMS: MenuItem[] = [
  // Luck Special
  it('Mysore', 'special', { Price: 140 }, 'मैसूर', 'મૈસુર'),
  it('Tawa Mysore', 'special', { Price: 160 }, 'तावा मैसूर', 'તાવા મૈસુર'),
  it('Cheese Paneer Tukda Mysore', 'special', { Price: 280 }, 'चीज़ पनीर टुकड़ा मैसूर', 'ચીઝ પનીર ટુકડા મૈસુર'),
  it('Cheese Mysore', 'special', { Price: 160 }, 'चीज़ मैसूर', 'ચીઝ મૈસુર'),
  it('Luck Sp. New Gotalo', 'special', { Price: 249 }, 'लक स्पे. न्यू गोटालो', 'લક સ્પે. ન્યુ ગોટાળો'),
  it('Luck Regular Gotalo', 'special', { Price: 249 }, 'लक रेगुलर गोटालो', 'લક રેગ્યુલર ગોટાળો'),
  it('Green Garlic Tadka (Gotalo)', 'special', { Price: 259 }, 'ग्रीन गार्लिक तड़का (गोटालो)', 'ગ્રીન ગાર્લિક તડકા (ગોટાળો)'),
  it('Luck Sp. Rajwadi Mysore', 'special', { Price: 230 }, 'लक स्पे. रजवाड़ी मैसूर', 'લક સ્પે. રજવાડી મૈસુર'),

  // Pavbhaji
  it('Pavbhaji', 'pavbhaji', { Price: 120 }, 'पावभाजी', 'પાંઉભાજી'),
  it('Cheese Pavbhaji', 'pavbhaji', { Price: 150 }, 'चीज़ पावभाजी', 'ચીઝ પાંઉભાજી'),
  it('Green Pavbhaji', 'pavbhaji', { Price: 160 }, 'ग्रीन पावभाजी', 'ગ્રીન પાંઉભાજી'),
  it('Extra Pav', 'pavbhaji', { 'Oil (4pc)': 20, 'Butter (4pc)': 30 }, 'एक्स्ट्रा पाव', 'એક્સ્ટ્રા પાંઉ'),

  // Pulao
  it('Veg. Pulao', 'pulao', { Price: 140 }, 'वेज. पुलाव', 'વેજ. પુલાવ'),
  it('Paneer Pulao', 'pulao', { Price: 160 }, 'पनीर पुलाव', 'પનીર પુલાવ'),
  it('Cheese Pulao', 'pulao', { Price: 190 }, 'चीज़ पुलाव', 'ચીઝ પુલાવ'),
  it('Haidrabadi Pulao', 'pulao', { Price: 199 }, 'हैदराबादी पुलाव', 'હૈદરાબાદી પુલાવ'),
  it('Kashmiri Pulao', 'pulao', { Price: 200 }, 'कश्मीरी पुलाव', 'કશ્મીરી પુલાવ'),

  // Dosa · Paper
  it('Chocolate Paper', 'dosa', { Butter: 140 }, 'चॉकलेट पेपर', 'ચોકલેટ પેપર'),
  it('Masala Dosa', 'dosa', { Oil: 100, Butter: 120 }, 'मसाला ढोसा', 'મસાલા ઢોસા'),
  it('Sada Paper', 'dosa', { Oil: 50, Butter: 60 }, 'सादा पेपर', 'સાદા પેપર'),
  it('Baby Paper', 'dosa', { Oil: 50, Butter: 60 }, 'बेबी पेपर', 'બેબી પેપર'),
  it('Nylon Paper', 'dosa', { Oil: 60, Butter: 70 }, 'नायलॉन पेपर', 'નાયલોન પેપર'),
  it('Garlic Paper', 'dosa', { Oil: 70, Butter: 80 }, 'गार्लिक पेपर', 'ગાર્લિક પેપર'),
  it('Baby Cheese Paper', 'dosa', { Price: 130 }, 'बेबी चीज़ पेपर', 'બેબી ચીઝ પેપર'),
  it('Cheese Paper', 'dosa', { Price: 130 }, 'चीज़ पेपर', 'ચીઝ પેપર'),
  it('Cheese Garlic Paper', 'dosa', { Price: 140 }, 'चीज़ गार्लिक पेपर', 'ચીઝ ગાર્લિક પેપર'),
  it('Cheese Nylon Paper', 'dosa', { Price: 140 }, 'चीज़ नायलॉन पेपर', 'ચીઝ નાયલોન પેપર'),
  it('Chilli Paper', 'dosa', { Price: 90 }, 'चिली पेपर', 'ચીલી પેપર'),
  it('Cheese Chilli Paper', 'dosa', { Price: 130 }, 'चीज़ चिली पेपर', 'ચીઝ ચીલી પેપર'),
  it('Chinese Dosa', 'dosa', { Price: 149 }, 'चाइनीज़ ढोसा', 'ચાઈનીઝ ઢોસા'),

  // Roll Cut Dosa
  it('Jiniroll Dhosa', 'rollcut', { Price: 199 }, 'जिनीरोल ढोसा', 'જીનીરોલ ઢોસા'),
  it('Rajarani Dhosa', 'rollcut', { Price: 199 }, 'राजारानी ढोसा', 'રાજારાની ઢોસા'),
  it('Dilkhush Dhosa', 'rollcut', { Price: 249 }, 'दिलखुश ढोसा', 'દિલખુશ ઢોસા'),
  it('Cheese Palak Dhosa', 'rollcut', { Price: 179 }, 'चीज़ पालक ढोसा', 'ચીઝ પાલક ઢોસા'),
  it('Palak Paneer Dhosa', 'rollcut', { Price: 199 }, 'पालक पनीर ढोसा', 'પાલક પનીર ઢોસા'),
  it('Cheese Palak Paneer Dhosa', 'rollcut', { Price: 220 }, 'चीज़ पालक पनीर ढोसा', 'ચીઝ પાલક પનીર ઢોસા'),
  it('Cheese Masala Dhosa', 'rollcut', { Price: 190 }, 'चीज़ मसाला ढोसा', 'ચીઝ મસાલા ઢોસા'),
  it('Cheese Paneer Dhosa', 'rollcut', { Price: 260 }, 'चीज़ पनीर ढोसा', 'ચીઝ પનીર ઢોસા'),

  // Special Roll Cut
  it('Pizza Dhosa', 'premroll', { Price: 280 }, 'पिज़्ज़ा ढोसा', 'પીઝા ઢોસા'),
  it('Paneer Chilli Dhosa', 'premroll', { Price: 260 }, 'पनीर चिली ढोसा', 'પનીર ચીલી ઢોસા'),

  // Soup
  it('Manchow Soup', 'soup', { Price: 100 }, 'मनचाउ सूप', 'મન્ચાઉ સૂપ'),
  it('Hot & Sour Soup', 'soup', { Price: 99 }, 'हॉट एंड सॉर सूप', 'હોટ એન્ડ સોર સૂપ'),

  // Manchurian
  it('Manchurian Dry', 'manchurian', { Half: 130, Full: 150 }, 'मंचूरियन ड्राय', 'મન્ચુરીયન ડ્રાય'),
  it('Manchurian Gravy', 'manchurian', { Half: 130, Full: 150 }, 'मंचूरियन ग्रेवी', 'મન્ચુરીયન ગ્રેવી'),
  it('Schezwan Manchurian', 'manchurian', { Full: 170 }, 'शेज़वान मंचूरियन', 'શેઝવાન મન્ચુરીયન'),
  it('Garlic Manchurian', 'manchurian', { Full: 170 }, 'गार्लिक मंचूरियन', 'ગાર્લિક મન્ચુરીયન'),
  it('Paneer Manchurian', 'manchurian', { Full: 199 }, 'पनीर मंचूरियन', 'પનીર મન્ચુરીયન'),

  // Rice
  it('Fried Rice', 'rice', { Price: 149 }, 'फ्राइड राइस', 'ફ્રાઈડ રાઈસ'),
  it('Manchurian Rice', 'rice', { Price: 149 }, 'मंचूरियन राइस', 'મન્ચુરીયન રાઈસ'),
  it('Singapuri Rice', 'rice', { Price: 180 }, 'सिंगापुरी राइस', 'સીંગાપુરી રાઈસ'),
  it('Schezwan Rice', 'rice', { Price: 170 }, 'शेज़वान राइस', 'શેઝવાન રાઈસ'),
  it('Triple Rice', 'rice', { Price: 199 }, 'ट्रिपल राइस', 'ટ્રીપલ રાઈસ'),
  it('Hong Kong Rice', 'rice', { Price: 150 }, 'हॉन्ग कॉन्ग राइस', 'હોંગ કોંગ રાઈસ'),
  it('Paneer Rice', 'rice', { Price: 199 }, 'पनीर राइस', 'પનીર રાઈસ'),

  // Noodles
  it('Hakka Noodles', 'noodles', { Price: 149 }, 'हक्का नूडल्स', 'હક્કા નુડલ્સ'),
  it('Manchurian Noodles', 'noodles', { Price: 149 }, 'मंचूरियन नूडल्स', 'મન્ચુરીયન નુડલ્સ'),
  it('Singapuri Noodles', 'noodles', { Price: 130 }, 'सिंगापुरी नूडल्स', 'સીંગાપુરી નુડલ્સ'),
  it('Schezwan Noodles', 'noodles', { Price: 130 }, 'शेज़वान नूडल्स', 'શેઝવાન નુડલ્સ'),

  // Bhel
  it('Chinese Bhel', 'bhel', { Price: 149 }, 'चाइनीज़ भेल', 'ચાઈનીઝ ભેલ'),
  it('Bombay Bhel', 'bhel', { Price: 149 }, 'बॉम्बे भेल', 'બોમ્બે ભેલ'),

  // Premium Chinese
  it('Paneer Crispy', 'chinese', { Price: 249 }, 'पनीर क्रिस्पी', 'પનીર ક્રિસ્પી'),
  it('Paneer Chilli Dry', 'chinese', { Price: 199 }, 'पनीर चिली ड्राय', 'પનીર ચીલી ડ્રાય'),
  it('Paneer Chilli Gravy', 'chinese', { Price: 190 }, 'पनीर चिली ग्रेवी', 'પનીર ચીલી ગ્રેવી'),
  it('Chinese Chopsy', 'chinese', { Price: 149 }, 'चाइनीज़ चॉप्सी', 'ચાઈનીઝ ચોપ્સી'),
  it('American Chopsy', 'chinese', { Price: 140 }, 'अमेरिकन चॉप्सी', 'અમેરીકન ચોપ્સી'),

  // Cold Drinks
  it('Sosyo', 'drinks', { Price: 20 }, 'सोस्यो', 'સોસ્યો'),
  it('Thums Up', 'drinks', { Price: 20 }, 'थम्स अप', 'થમ્સ અપ'),
  it('Sprite', 'drinks', { Price: 20 }, 'स्प्राइट', 'સ્પ્રાઈટ'),
  it('Fanta', 'drinks', { Price: 20 }, 'फैंटा', 'ફેન્ટા'),
  it('Maaza', 'drinks', { Price: 20 }, 'माज़ा', 'માઝા'),
  it('Mineral Water 500ml', 'drinks', { Price: 10 }, 'मिनरल वॉटर 500ml', 'મીનરલ વોટર 500ml'),
  it('Mineral Water 1 Litre', 'drinks', { Price: 20 }, 'मिनरल वॉटर 1 लीटर', 'મીનરલ વોટર ૧ લિટર'),
  it('Masala Chaas', 'drinks', { Price: 20 }, 'मसाला छाछ', 'મસાલા છાશ'),
];

/** Dish name in the requested language, falling back to what the menu prints. */
export function itemName(item: MenuItem | undefined, lang: Lang): string {
  if (!item) return '';
  if (lang === 'hi' && item.nameHi) return item.nameHi;
  if (lang === 'gu' && item.nameGu) return item.nameGu;
  return item.name;
}

/**
 * The name to print for a sold line, in the requested language. Reads the
 * snapshot on the line, so it is stable for the life of the order.
 */
export function lineName(
  line: { name: string; nameHi?: string; nameGu?: string },
  lang: Lang,
): string {
  if (lang === 'hi' && line.nameHi) return line.nameHi;
  if (lang === 'gu' && line.nameGu) return line.nameGu;
  return line.name;
}

export function sortItems(list: MenuItem[]): MenuItem[] {
  return [...list].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
}
